import React, { useState } from 'react';
import { Modal } from '@material-ui/core';
import {
  ArrowLeft,
  X,
  UploadSimple,
  File,
  CheckCircle,
  XCircle,
} from 'phosphor-react';
import {
  closeFileUploadModal,
  openPreapprovedTrueModal,
  showUploadSuccessModal,
  uploadPreapprovalDocument,
} from '../../../../../actions/dashboard';
import { PaperClipBlob } from '../../../../utility/Icons';
import styles from '../../../../../styles/sass/components/GetPreapproved.module.scss';
import { connect } from 'react-redux';
import { updateProfile } from '../../../../../actions/auth';

const maxAllowedSize = 9437184; // Bytes 9MB
let interval: NodeJS.Timeout;
interface Props {
  modalStyle: any;
  token: string;
  fileUploadModalOpen: boolean;
  dispatch: Function;
  preapproved: String;
  user: any;
}

export const FileUploadModal: React.FC<Props> = ({
  modalStyle,
  token,
  fileUploadModalOpen,
  dispatch,
  preapproved,
  user,
}) => {
  const [currentProgress, setProgress] = useState(0);
  const [showProgressbar, toggleProgressBar] = useState(false);
  const [file, setFile] = useState<any>();
  const [finishedUploading, setFinishedUploading] = useState(false);
  const [alertText, setAlert] = useState<string | undefined>(undefined);

  React.useEffect(() => {
    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    if (showProgressbar) {
      // Logic to show progress animation
      interval = setInterval(() => {
        setProgress(preValue => {
          if (preValue < 90) {
            return preValue + 10;
          } else {
            clearInterval(interval);
            return preValue;
          }
        });
      }, 500);
    } else {
      clearInterval(interval);
    }
  }, [showProgressbar]);

  let fileInputRef: any;

  const handleAddFiles = () => {
    fileInputRef.click();
  };

  const closeCurrentModal = () => {
    clearInterval(interval);
    setProgress(0);
    toggleProgressBar(false);
    setFile(null);
    setFinishedUploading(false);
    setAlert(undefined);
    dispatch(closeFileUploadModal());
  };

  const handleBackButton = () => {
    closeCurrentModal();
    dispatch(openPreapprovedTrueModal());
  };

  const handleCompleteUpload = () => {
    dispatch(
      updateProfile(
        { onboarding: { ...user.onboarding, preapprovalUploaded: true } },
        token,
        false,
        true,
      ),
    );
    dispatch(closeFileUploadModal());
    dispatch(showUploadSuccessModal());
  };

  const handleDeleteFile = (deleteFromApi?: boolean) => {
    if (deleteFromApi) {
      // Delete the uploaded file from API
      dispatch(updateProfile({ preapprovalUrl: null }, token, false, true));
    }
    toggleProgressBar(false);
    setFinishedUploading(false);
    setProgress(0);
    setFile(null);
  };

  const onCompleteCallback = (success: boolean) => {
    if (success) {
      setProgress(100);
      setTimeout(() => {
        setFinishedUploading(true);
      }, 1000);
    } else {
      clearInterval(interval);
      setProgress(0);
      setTimeout(() => {
        handleDeleteFile();
        setAlert('Something went wrong. Please try again.');
      }, 1000);
    }
  };

  const handleUpload = async (event: any) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) {
      setFile(undefined);
      return;
    }
    if (selectedFile.size > maxAllowedSize) {
      setAlert('Oops! Exceeded maximum file size');
      handleDeleteFile();
      return;
    } else {
      setAlert(undefined);
      let reader = new FileReader();
      reader.onload = () => {
        const fileSrc = reader.result;
        const preapprovalDocument: any = {};
        preapprovalDocument.name = selectedFile.name;
        preapprovalDocument.src = fileSrc;
        setFile(preapprovalDocument);
        setFinishedUploading(false);
        toggleProgressBar(true);
        dispatch(
          uploadPreapprovalDocument(
            { preapprovalDocument },
            token,
            onCompleteCallback,
          ),
        );
      };
      reader.readAsDataURL(selectedFile);
    }
    event.target.value = '';
  };

  return (
    <Modal
      open={fileUploadModalOpen}
      onClose={closeCurrentModal}
      style={modalStyle}
    >
      <div className={styles.fileUploadModalContainer}>
        <div className={styles.header}>
          {preapproved === 'Yes' && (
            <div className={styles.backButton}>
              <button className={styles.altButton} onClick={handleBackButton}>
                <ArrowLeft
                  size={32}
                  color="#463264"
                  weight="regular"
                  style={{ marginRight: 5 }}
                />{' '}
              </button>
              Back{' '}
            </div>
          )}
          <button
            className={styles.altButton}
            onClick={() => closeCurrentModal()}
            style={{ marginLeft: preapproved !== 'Yes' ? 'auto' : '' }}
          >
            <X size={32} color="#323232" weight="regular" />
          </button>
        </div>
        <PaperClipBlob
          width="66"
          height="60"
          style={{
            margin: preapproved !== 'Yes' ? '0px 0px 20px 0px' : '20px 0',
          }}
        />
        <div className={styles.mainText}>Upload Documents</div>
        <div className={styles.mainText} style={{ fontSize: 16 }}>
          What you need to get pre-approved:
          <ul className={styles.subText}>
            <li style={{ listStyleType: 'disc', margin: '10px 10px' }}>
              Your preapproval letter{' '}
            </li>
          </ul>
        </div>

        <div className={styles.uploadContainer}>
          <div className={styles.uploadPhotoContainer}>
            <UploadSimple size={53} color="#737373" weight="bold" />
          </div>
          <div className={styles.uploadContainerText}>
            JPEG, PNG, or PDF, smaller than 10MB
          </div>
          <input
            type="file"
            hidden={true}
            accept="image/png, image/jpeg, .pdf"
            ref={refParam => (fileInputRef = refParam)}
            onChange={handleUpload}
            size={maxAllowedSize}
          />
          <button onClick={handleAddFiles} className={styles.uploadButton}>
            Choose File & Upload
          </button>
        </div>
        {alertText && (
          <div className={styles.progressContainer}>
            <p className={styles.alertText}>{alertText}</p>
          </div>
        )}
        {showProgressbar && (
          <div className={styles.progressContainer}>
            <div className={styles.fileName}>
              <div className={styles.leftPart}>
                {finishedUploading && (
                  <button
                    className={styles.deleteFileButton}
                    onClick={() => handleDeleteFile(true)}
                  >
                    <XCircle size={24} color="#443461" />
                  </button>
                )}
                {finishedUploading ? (
                  <File size={24} color="#443461" weight="fill" />
                ) : (
                  <File size={24} color="#443461" weight="regular" />
                )}
                <p>{file.name}</p>
              </div>
              <div className={styles.rightPart}>
                {finishedUploading ? (
                  <CheckCircle size={24} color="#2C5853" weight="fill" />
                ) : (
                  <div>{`Uploading - ${currentProgress}%`}</div>
                )}
              </div>
            </div>
            {!finishedUploading && (
              <div className={styles.progressBar}>
                <div
                  className={styles.progressDone}
                  style={{ width: `${currentProgress}%`, opacity: 1 }}
                >
                  &nbsp;
                </div>
              </div>
            )}
            {finishedUploading && (
              <button
                className={styles.uploadDocumentsButton}
                onClick={handleCompleteUpload}
              >
                Finish
              </button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: any) => ({
  fileUploadModalOpen: state.dashboard.fileUploadModalOpen,
  preapproved: state.signupSurvey.signupSurveyDetails.preapproved,
  user: state.auth.user,
});

export default connect(mapStateToProps)(FileUploadModal);
